import type { Pagination } from '~/core/entities/api/ApiPagination';
import type { Pluscodes } from '~/core/entities/api/ApiPluscodes';
import type {
	FeaturedVideosClustersResponse,
	Property,
	ShortVideoProperty
} from '~/core/entities/property';
import type {
	FeaturedVideosRecommendationType,
	PaginatedPropertyFilters,
	ShortsFilters
} from '~/core/entities/property/filters';
import type { ResultTuple } from '~~/core/helpers/result';
import type { LocationSlugFilters } from '~~/core/ports/repositories';
import type { NuxtAddressesApiReturn } from '~~/server/api/properties/addresses.get';

import { type CookieData } from '~/composables/useCookieData';
import { buildFeaturedVideosQueryParams } from '~/core/entities/property/featuredVideosQueryParams';
import { Result } from '~~/core/helpers/result';
import { createOriginalErrorObject } from '~~/server/utils/errorHandling';
import { prepareCookieHeaders } from '~~/utils/prepareCookieHeaders';

// ofetch has no default timeout; bound requests so a hung upstream can't stall
// SSR until the gateway gives up with a 5xx. 8s is above normal latency but
// under the gateway timeout, so we degrade gracefully instead.
const API_REQUEST_TIMEOUT_MS = 8000;

type NuxtListPropertiesApiReturn = {
	properties: Property[];
	pagination: Pagination;
	pluscodes?: Pluscodes;
	locationSlugFilters?: LocationSlugFilters;
	allWatched?: boolean;
	totalAvailable?: number;
};

type NuxtShortsApiReturn = {
	properties: ShortVideoProperty[];
	pagination: Pagination;
	allWatched?: boolean;
	totalAvailable?: number;
	counts?: {
		totalWithVideos: number;
		totalWithoutVideos: number;
	};
	sectionTitle?: string;
	recommendationType?: FeaturedVideosRecommendationType;
};

interface NuxtPropertiesRepository {
	list(
		filters: Partial<PaginatedPropertyFilters>,
		cookies?: CookieData
	): Promise<ResultTuple<NuxtListPropertiesApiReturn>>;
	getOne(
		identifier: string,
		markUserWishlistsId?: string,
		cookies?: CookieData
	): Promise<ResultTuple<Property>>;
	checkOne(
		identifier: string,
		cookies?: CookieData
	): Promise<
		ResultTuple<{
			commercialId: string;
			slug: Property['slug'];
			publicationStatus: Property['publicationStatus'];
		} | null>
	>;
	getAddresses(
		cookies?: CookieData
	): Promise<ResultTuple<NuxtAddressesApiReturn | ''>>;
	getSimilarProperties(
		params: {
			identifier: string;
			propertyType?: PaginatedPropertyFilters['propertyTypes'];
			region?: string;
			markUserWishlistsId?: string;
			page?: number;
			perPage?: number;
		},
		cookies?: CookieData
	): Promise<ResultTuple<NuxtListPropertiesApiReturn>>;
	getShorts(
		params: ShortsFilters,
		cookies?: CookieData
	): Promise<ResultTuple<NuxtShortsApiReturn>>;
	getShortsClusters(
		referenceProperty: string,
		cookies?: CookieData
	): Promise<ResultTuple<FeaturedVideosClustersResponse>>;
	getChosenForYouProperties(
		params: {
			identifier: string;
			regions?: string[];
			askingPrice?: number;
			markUserWishlistsId?: string;
			excludeFeaturedVideos?: boolean;
		},
		cookies?: CookieData
	): Promise<ResultTuple<Property[]>>;
}

export const nuxtPropertiesRepository: NuxtPropertiesRepository = {
	async list(filters, cookies) {
		try {
			const headers = prepareCookieHeaders(cookies);
			const response = await $fetch('/api/properties', {
				method: 'GET',
				query: filters,
				headers,
				timeout: API_REQUEST_TIMEOUT_MS
			});

			return Result.ok(response);
		} catch (error) {
			return Result.error({
				message: 'Error while fetching properties from the API.',
				source: 'nuxtPropertiesRepository.list',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	},
	async getOne(identifier, markUserWishlistsId, cookies) {
		try {
			const headers = prepareCookieHeaders(cookies);
			const response = await $fetch<Property>(`/api/properties/${identifier}`, {
				method: 'GET',
				query: { markUserWishlistsId },
				headers,
				timeout: API_REQUEST_TIMEOUT_MS
			});

			return Result.ok(response);
		} catch (error) {
			return Result.error({
				message: `Error while fetching property with identifier ${identifier} from the API.`,
				source: 'nuxtPropertiesRepository.getOne',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	},
	async checkOne(identifier, cookies) {
		try {
			const headers = prepareCookieHeaders(cookies);
			const response = await $fetch<{
				commercialId: string;
				slug: Property['slug'];
				publicationStatus: Property['publicationStatus'];
			} | null>(`/api/properties/${identifier}/check`, {
				method: 'GET',
				headers
			});

			return Result.ok(response);
		} catch (error) {
			return Result.error({
				message: `Error while fetching property with identifier ${identifier} from the API.`,
				source: 'nuxtPropertiesRepository.getOne',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	},
	async getSimilarProperties(
		{
			identifier,
			propertyType,
			region,
			markUserWishlistsId,
			page = 1,
			perPage = 6
		},
		cookies
	) {
		try {
			const headers = prepareCookieHeaders(cookies);
			const [similarProperties, similarPropertiesError] = await $fetch<
				ResultTuple<NuxtListPropertiesApiReturn>
			>(`/api/properties/${identifier}/similar-properties`, {
				query: {
					propertyType: propertyType,
					region,
					markUserWishlistsId,
					page,
					perPage
				},
				headers
			});

			if (!similarProperties || similarPropertiesError) {
				return Result.error({
					message: `Error while fetching similar properties from the API.`,
					source: 'nuxtPropertiesRepository.getSimilarProperties',
					originalErrorObject: {
						...similarPropertiesError,
						name: 'similarPropertiesError'
					}
				});
			}

			return Result.ok(similarProperties);
		} catch (error) {
			return Result.error({
				message: `Error while fetching similar properties from the API.`,
				source: 'nuxtPropertiesRepository.getSimilarProperties',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	},
	async getShorts(
		{
			excludeProperties = [],
			markUserWishlistsId,
			page = 1,
			perPage = 12,
			cities,
			states,
			macroregions,
			regions,
			propertyTypes,
			minAskingPrice,
			maxAskingPrice,
			minArea,
			maxArea,
			bedrooms,
			suites,
			bathrooms,
			parkingSpots,
			features,
			displayInHomeShorts,
			referenceProperty,
			recommendationStrategy,
			expandSearch,
			propertyTypeGroup,
			radius,
			orderBy,
			sortBy,
			poiLocationLat,
			poiLocationLng,
			onlyUnwatched,
			includeImagesEvery
		},
		cookies
	) {
		try {
			const headers = prepareCookieHeaders(cookies);
			const [shortsResult, shortsError] = await $fetch<
				ResultTuple<NuxtShortsApiReturn>
			>('/api/properties/featured-videos', {
				query: buildFeaturedVideosQueryParams({
					excludeProperties,
					markUserWishlistsId,
					page,
					perPage,
					cities,
					states,
					macroregions,
					regions,
					propertyTypes,
					minAskingPrice,
					maxAskingPrice,
					minArea,
					maxArea,
					bedrooms,
					suites,
					bathrooms,
					parkingSpots,
					features,
					displayInHomeShorts,
					...(referenceProperty !== undefined && { referenceProperty }),
					...(recommendationStrategy !== undefined && {
						recommendationStrategy
					}),
					...(expandSearch !== undefined && { expandSearch }),
					...(propertyTypeGroup?.length && { propertyTypeGroup }),
					...(radius !== undefined && { radius }),
					orderBy,
					sortBy,
					poiLocationLat,
					poiLocationLng,
					onlyUnwatched,
					includeImagesEvery
				}),
				headers
			});

			if (!shortsResult || shortsError) {
				return Result.error({
					message: `Error while fetching shorts from the API.`,
					source: 'nuxtPropertiesRepository.getShorts',
					originalErrorObject: {
						...shortsError,
						name: 'shortsError'
					}
				});
			}

			return Result.ok(shortsResult);
		} catch (error) {
			return Result.error({
				message: `Error while fetching shorts from the API.`,
				source: 'nuxtPropertiesRepository.getShorts',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	},
	async getShortsClusters(referenceProperty, cookies) {
		try {
			const headers = prepareCookieHeaders(cookies);
			const response = await $fetch<FeaturedVideosClustersResponse>(
				'/api/properties/featured-videos/clusters',
				{
					method: 'GET',
					query: { referenceProperty },
					headers,
					timeout: API_REQUEST_TIMEOUT_MS
				}
			);

			return Result.ok(response);
		} catch (error) {
			return Result.error({
				message: 'Error while fetching featured videos clusters from the API.',
				source: 'nuxtPropertiesRepository.getShortsClusters',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	},
	async getChosenForYouProperties(
		{
			identifier,
			regions,
			askingPrice,
			markUserWishlistsId,
			excludeFeaturedVideos
		},
		cookies
	) {
		try {
			const headers = prepareCookieHeaders(cookies);
			const [chosenForYouProperties, chosenForYouPropertiesError] =
				await $fetch<ResultTuple<Property[]>>(
					`/api/properties/${identifier}/chosen-for-you`,
					{
						query: {
							regions,
							askingPrice,
							markUserWishlistsId,
							excludeFeaturedVideos
						},
						headers
					}
				);

			if (!chosenForYouProperties || chosenForYouPropertiesError) {
				return Result.error({
					message: `Error while fetching chosen for you properties from the API.`,
					source: 'nuxtPropertiesRepository.getChosenForYouProperties',
					originalErrorObject: {
						...chosenForYouPropertiesError,
						name: 'chosenForYouPropertiesError'
					} as Error
				});
			}

			return Result.ok(chosenForYouProperties);
		} catch (error) {
			return Result.error({
				message: `Error while fetching chosen for you properties from the API.`,
				source: 'nuxtPropertiesRepository.getChosenForYouProperties',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	},
	async getAddresses(cookies) {
		try {
			const headers = prepareCookieHeaders(cookies);
			const response = await $fetch(`/api/properties/addresses`, {
				method: 'GET',
				headers
			});

			if (!response)
				return Result.error({
					message: `Error while fetching properties addresses from the API.`,
					source: 'nuxtPropertiesRepository.getAddresses'
				});

			return Result.ok(response);
		} catch (error) {
			return Result.error({
				message: `Error while fetching properties addresses from the API.`,
				source: 'nuxtPropertiesRepository.getAddresses',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	}
};
